// Display prices per (plan, currency, period). These are *display* values only —
// the authoritative amount is charged by Stripe via the Price ID (see currency.ts).
// Keep these in sync with the Stripe dashboard.

import type { Currency, PlanSlug } from './currency'
import { currencyForLanguage } from './currency'

type PriceCell = { monthly: string; yearly: string; save: string }

export const PLAN_PRICES: Record<PlanSlug, Record<Currency, PriceCell>> = {
  starter: {
    DKK: { monthly: '79 kr', yearly: '790 kr', save: '158 kr' },
    USD: { monthly: '$11', yearly: '$110', save: '$22' },
    EUR: { monthly: '€11', yearly: '€110', save: '€22' },
    PLN: { monthly: '45 zł', yearly: '450 zł', save: '90 zł' },
    SEK: { monthly: '99 kr', yearly: '990 kr', save: '198 kr' },
  },
  author: {
    DKK: { monthly: '199 kr', yearly: '1.990 kr', save: '398 kr' },
    USD: { monthly: '$29', yearly: '$290', save: '$58' },
    EUR: { monthly: '€29', yearly: '€290', save: '€58' },
    PLN: { monthly: '119 zł', yearly: '1.190 zł', save: '238 zł' },
    SEK: { monthly: '249 kr', yearly: '2.490 kr', save: '498 kr' },
  },
  pro: {
    DKK: { monthly: '449 kr', yearly: '4.490 kr', save: '898 kr' },
    USD: { monthly: '$59', yearly: '$590', save: '$118' },
    EUR: { monthly: '€59', yearly: '€590', save: '€118' },
    PLN: { monthly: '249 zł', yearly: '2.490 zł', save: '498 zł' },
    SEK: { monthly: '549 kr', yearly: '5.490 kr', save: '1.098 kr' },
  },
}

/** Internal landing/checkout card slug → canonical paid plan slug. */
export const CARD_SLUG_TO_PLAN: Record<string, PlanSlug> = {
  basic: 'starter',
  starter: 'starter',
  pro: 'author', // legacy card slug "pro" is the mid (Author) card
  author: 'author',
  publisher: 'pro', // legacy card slug "publisher" is the top (Pro) card
}

const YEARLY_CADENCE: Record<string, string> = {
  EN: '/yr', DA: '/år', DE: '/Jahr', FR: '/an', IT: '/anno',
  ES: '/año', PT: '/ano', NL: '/jaar', SE: '/år', PL: '/rok',
}

export function yearlyCadenceForLang(lang: string): string {
  return YEARLY_CADENCE[String(lang || 'EN').toUpperCase()] ?? YEARLY_CADENCE.EN
}

const SAVE_LABEL: Record<string, (amt: string) => string> = {
  EN: (a) => `Save ${a} per year`,
  DA: (a) => `Spar ${a} om året`,
  DE: (a) => `Spare ${a} pro Jahr`,
  ES: (a) => `Ahorra ${a} al año`,
  FR: (a) => `Économisez ${a} par an`,
  IT: (a) => `Risparmia ${a} all'anno`,
  NL: (a) => `Bespaar ${a} per jaar`,
  PL: (a) => `Oszczędź ${a} rocznie`,
  PT: (a) => `Poupe ${a} por ano`,
  SE: (a) => `Spara ${a}/år`,
}

export function saveLabel(lang: string, amount: string): string {
  const fn = SAVE_LABEL[String(lang || 'EN').toUpperCase()] || SAVE_LABEL.EN
  return fn(amount)
}

// ── Trial banner copy (10 languages) ──────────────────────────────
export type TrialCopy = { badge: string; title: string; desc: string; cta: string }

export const TRIAL_BANNER: Record<string, TrialCopy> = {
  DA: { badge: '7 DAGE GRATIS', title: 'Prøv BookBuilderGo gratis', desc: 'Skriv 1 fuld bog og lav 1 cover — fuld adgang til alle værktøjer i 7 dage. Intet kreditkort.', cta: 'Start gratis →' },
  EN: { badge: '7 DAYS FREE', title: 'Try BookBuilderGo free', desc: 'Write 1 full book and create 1 cover — full access to all tools for 7 days. No credit card.', cta: 'Start free →' },
  DE: { badge: '7 TAGE KOSTENLOS', title: 'BookBuilderGo kostenlos testen', desc: 'Schreibe 1 ganzes Buch und erstelle 1 Cover — voller Zugriff auf alle Tools für 7 Tage. Keine Kreditkarte.', cta: 'Kostenlos starten →' },
  ES: { badge: '7 DÍAS GRATIS', title: 'Prueba BookBuilderGo gratis', desc: 'Escribe 1 libro completo y crea 1 portada — acceso completo a todas las herramientas durante 7 días. Sin tarjeta de crédito.', cta: 'Empezar gratis →' },
  FR: { badge: '7 JOURS GRATUITS', title: 'Essayez BookBuilderGo gratuitement', desc: 'Écrivez 1 livre entier et créez 1 couverture — accès complet à tous les outils pendant 7 jours. Sans carte de crédit.', cta: 'Commencer gratuitement →' },
  IT: { badge: '7 GIORNI GRATIS', title: 'Prova BookBuilderGo gratis', desc: 'Scrivi 1 libro completo e crea 1 copertina — accesso completo a tutti gli strumenti per 7 giorni. Nessuna carta di credito.', cta: 'Inizia gratis →' },
  NL: { badge: '7 DAGEN GRATIS', title: 'Probeer BookBuilderGo gratis', desc: 'Schrijf 1 volledig boek en maak 1 omslag — volledige toegang tot alle tools gedurende 7 dagen. Geen creditcard.', cta: 'Gratis starten →' },
  PL: { badge: '7 DNI ZA DARMO', title: 'Wypróbuj BookBuilderGo za darmo', desc: 'Napisz 1 pełną książkę i stwórz 1 okładkę — pełny dostęp do wszystkich narzędzi przez 7 dni. Bez karty kredytowej.', cta: 'Rozpocznij za darmo →' },
  PT: { badge: '7 DIAS GRÁTIS', title: 'Experimente BookBuilderGo grátis', desc: 'Escreva 1 livro completo e crie 1 capa — acesso completo a todas as ferramentas por 7 dias. Sem cartão de crédito.', cta: 'Começar grátis →' },
  SE: { badge: '7 DAGAR GRATIS', title: 'Prova BookBuilderGo gratis', desc: 'Skriv 1 hel bok och skapa 1 omslag — full tillgång till alla verktyg i 7 dagar. Inget kreditkort.', cta: 'Börja gratis →' },
}

export function trialCopy(lang: string): TrialCopy {
  return TRIAL_BANNER[String(lang || 'EN').toUpperCase()] || TRIAL_BANNER.EN
}

// ── Monthly / yearly toggle copy (10 languages) ───────────────────
export type ToggleCopy = { monthly: string; yearly: string; freeMonths: string }

export const TOGGLE_LABELS: Record<string, ToggleCopy> = {
  DA: { monthly: 'Månedlig', yearly: 'Årlig', freeMonths: '2 mdr gratis' },
  EN: { monthly: 'Monthly', yearly: 'Yearly', freeMonths: '2 mo. free' },
  DE: { monthly: 'Monatlich', yearly: 'Jährlich', freeMonths: '2 Mon. gratis' },
  ES: { monthly: 'Mensual', yearly: 'Anual', freeMonths: '2 meses gratis' },
  FR: { monthly: 'Mensuel', yearly: 'Annuel', freeMonths: '2 mois gratuits' },
  IT: { monthly: 'Mensile', yearly: 'Annuale', freeMonths: '2 mesi gratis' },
  NL: { monthly: 'Maandelijks', yearly: 'Jaarlijks', freeMonths: '2 mnd gratis' },
  PL: { monthly: 'Miesięcznie', yearly: 'Rocznie', freeMonths: '2 m-ce za darmo' },
  PT: { monthly: 'Mensal', yearly: 'Anual', freeMonths: '2 meses grátis' },
  SE: { monthly: 'Månadsvis', yearly: 'Årligen', freeMonths: '2 mån gratis' },
}

export function toggleCopy(lang: string): ToggleCopy {
  return TOGGLE_LABELS[String(lang || 'EN').toUpperCase()] || TOGGLE_LABELS.EN
}

/** Resolve display price + save badge for a card slug, language and period. */
export function priceForCard(cardSlug: string, lang: string, period: 'monthly' | 'yearly') {
  const plan = CARD_SLUG_TO_PLAN[cardSlug] || 'starter'
  const currency = currencyForLanguage(lang)
  const cell = PLAN_PRICES[plan][currency]
  return {
    plan,
    currency,
    price: period === 'yearly' ? cell.yearly : cell.monthly,
    save: cell.save,
    saveText: saveLabel(lang, cell.save),
  }
}
